/**
 * 애플리케이션 버전 표시 컴포넌트
 */
import { APP_VERSION, BUILD_DATE } from '../version.js';

/**
 * 날짜를 YYYY-MM-DD 형식으로 포맷팅하는 함수
 * @param {Date} date - 날짜 객체
 * @returns {string} 포맷팅된 날짜 문자열
 */
function formatDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * 현재 다크모드 여부를 확인하는 함수
 * @returns {boolean} 다크모드 적용 여부
 */
function isDarkModeEnabled() {
  // data-theme 속성 확인
  const docTheme = document.documentElement.getAttribute('data-theme');
  if (docTheme === 'dark') {
    return true;
  }
  
  // body에 dark-mode 클래스가 있는지 확인
  const bodyHasDarkClass = document.body.classList.contains('dark-mode');
  
  // localStorage에 theme 설정이 있는지 확인
  const localStorageTheme = localStorage.getItem('sns-theme-preference') === 'dark';
  
  // 시스템 다크모드 설정 확인
  const systemDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  
  return bodyHasDarkClass || localStorageTheme || systemDarkMode;
}

/**
 * 버전 정보 모달 생성 및 표시
 * @param {string} version - 앱 버전
 * @param {string} buildDate - 빌드 날짜 (ISO 형식)
 */
function showVersionModal(version, buildDate) {
  // 기존 모달이 있다면 제거
  const existingModal = document.getElementById('versionInfoModal');
  if (existingModal) {
    existingModal.remove();
  }
  
  // 다크모드 상태 확인
  const darkModeEnabled = isDarkModeEnabled();
  
  // 다크모드 클래스 및 스타일 설정
  const darkModeClass = darkModeEnabled ? 'dark-theme' : '';
  const modalContentClass = darkModeEnabled ? 'bg-dark text-light' : '';
  const modalHeaderClass = darkModeEnabled ? 'border-secondary' : '';
  const modalFooterClass = darkModeEnabled ? 'border-secondary' : '';
  
  // 테이블 다크모드 스타일링
  const tableStyle = darkModeEnabled ? 
    'background-color: #343a40; color: #f8f9fa; border-color: #495057;' : '';
  const thStyle = darkModeEnabled ? 
    'background-color: #212529; color: #f8f9fa; border-color: #495057;' : '';
  const tdStyle = darkModeEnabled ? 
    'background-color: #343a40; color: #f8f9fa; border-color: #495057;' : '';

  // 모달 HTML 생성
  const modalHtml = `
    <div class="modal fade ${darkModeClass}" id="versionInfoModal" tabindex="-1" aria-labelledby="versionInfoModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content ${modalContentClass}">
          <div class="modal-header ${modalHeaderClass}">
            <h5 class="modal-title" id="versionInfoModalLabel">애플리케이션 정보</h5>
            <button type="button" class="btn-close ${darkModeEnabled ? 'btn-close-white' : ''}" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="text-center mb-3">
              <img src="/assets/icons/icon-192x192.png" alt="앱 아이콘" style="width: 80px; height: 80px; margin-bottom: 15px;">
              <h4>SNS PWA</h4>
            </div>
            <table class="table table-bordered ${darkModeEnabled ? 'table-dark' : ''}" style="${tableStyle}">
              <tbody>
                <tr>
                  <th scope="row" style="width: 40%; ${thStyle}">버전</th>
                  <td style="${tdStyle}">${version}</td>
                </tr>
                <tr>
                  <th scope="row" style="${thStyle}">빌드 날짜</th>
                  <td style="${tdStyle}">${new Date(buildDate).toLocaleString()}</td>
                </tr>
                <tr>
                  <th scope="row" style="${thStyle}">개발자</th>
                  <td style="${tdStyle}">SNS 개발팀</td>
                </tr>
                <tr>
                  <th scope="row" style="${thStyle}">라이선스</th>
                  <td style="${tdStyle}">MIT</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="modal-footer ${modalFooterClass}">
            <button type="button" class="btn ${darkModeEnabled ? 'btn-outline-light' : 'btn-primary'}" data-bs-dismiss="modal">확인</button>
          </div>
        </div>
      </div>
    </div>
  `;

  // 모달 요소를 body에 추가
  document.body.insertAdjacentHTML('beforeend', modalHtml);
  
  // 다크모드일 때 추가 스타일 적용
  if (darkModeEnabled) {
    const style = document.createElement('style');
    style.id = 'version-modal-dark-style';
    style.textContent = `
      #versionInfoModal .table-dark {
        background-color: #343a40;
        color: #f8f9fa;
        border-color: #495057;
      }
      #versionInfoModal .table-dark th,
      #versionInfoModal .table-dark td {
        border-color: #495057;
      }
      #versionInfoModal .table-dark tbody tr:nth-of-type(odd) {
        background-color: rgba(255, 255, 255, 0.05);
      }
    `;
    document.head.appendChild(style);
  }
  
  // 모달 인스턴스 생성 및 표시
  const modalElement = document.getElementById('versionInfoModal');
  
  try {
    // bootstrap 객체가 사용 가능한지 확인
    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
      
      // 모달이 닫힐 때 DOM에서 제거
      modalElement.addEventListener('hidden.bs.modal', () => {
        const darkStyle = document.getElementById('version-modal-dark-style');
        if (darkStyle) darkStyle.remove();
        modalElement.remove();
      });
    } else {
      console.error('Bootstrap Modal 객체를 찾을 수 없습니다. 모달을 수동으로 표시합니다.');
      // Bootstrap이 없는 경우 대체 로직 - 모달을 직접 표시
      modalElement.classList.add('show');
      modalElement.style.display = 'block';
      modalElement.setAttribute('aria-modal', 'true');
      modalElement.setAttribute('role', 'dialog');
      document.body.classList.add('modal-open');
      
      // 모달 배경 생성
      const backdrop = document.createElement('div');
      backdrop.className = 'modal-backdrop fade show';
      document.body.appendChild(backdrop);
      
      // 닫기 버튼 기능 추가
      const closeButtons = modalElement.querySelectorAll('[data-bs-dismiss="modal"]');
      closeButtons.forEach(button => {
        button.addEventListener('click', () => {
          const darkStyle = document.getElementById('version-modal-dark-style');
          if (darkStyle) darkStyle.remove();
          modalElement.classList.remove('show');
          modalElement.style.display = 'none';
          document.body.classList.remove('modal-open');
          backdrop.remove();
          modalElement.remove();
        });
      });
    }
  } catch (error) {
    console.error('모달 표시 중 오류 발생:', error);
    alert(`애플리케이션 정보: 버전 ${version}, 빌드일: ${new Date(buildDate).toLocaleString()}`);
    const darkStyle = document.getElementById('version-modal-dark-style');
    if (darkStyle) darkStyle.remove();
  }
}

/**
 * 애플리케이션 버전 정보를 UI에 표시
 */
export function initVersionDisplay() {
  console.log('버전 정보 표시 초기화 중...');
  
  // 빌드 날짜를 Date 객체로 변환
  const buildDate = new Date(BUILD_DATE);
  const formattedDate = formatDate(buildDate);
  
  // 푸터에 버전 정보 표시
  const footer = document.querySelector('footer');
  if (footer) {
    // 기존 버전 정보 요소가 있는지 확인
    let versionElement = document.getElementById('app-version');
    if (!versionElement) {
      versionElement = document.createElement('div');
      versionElement.id = 'app-version';
      versionElement.className = 'version-info text-center small py-2';
      footer.appendChild(versionElement);
    }
    
    // 다크모드 상태에 따라 텍스트 클래스 변경
    updateVersionElementTheme(versionElement);
    
    // 버전 정보 표시
    versionElement.innerHTML = `버전 ${APP_VERSION} (빌드일: ${formattedDate})`;
    
    // 커서 스타일을 pointer로 변경하여 클릭 가능함을 표시
    versionElement.style.cursor = 'pointer';
    
    // 클릭 이벤트 리스너 추가 (기존 리스너 제거 후 추가)
    versionElement.removeEventListener('click', () => showVersionModal(APP_VERSION, BUILD_DATE));
    versionElement.addEventListener('click', () => {
      console.log('버전 정보 클릭됨, 모달 표시 시도...');
      showVersionModal(APP_VERSION, BUILD_DATE);
    });
    
    // 테마 변경 감지를 위한 MutationObserver 설정
    setupThemeChangeObserver(versionElement);
    
    console.log('버전 정보 표시 초기화 완료:', versionElement);
  } else {
    console.warn('푸터 요소를 찾을 수 없어 버전 정보를 표시할 수 없습니다.');
    
    // 푸터가 없는 경우 body에 직접 추가
    const body = document.body;
    const versionElement = document.createElement('div');
    versionElement.id = 'app-version';
    versionElement.className = 'version-info text-center small py-2 position-fixed bottom-0 start-0 end-0';
    versionElement.style.zIndex = '1000';
    versionElement.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
    versionElement.style.cursor = 'pointer';
    
    // 다크모드 상태에 따라 텍스트 클래스 변경
    updateVersionElementTheme(versionElement);
    
    versionElement.innerHTML = `버전 ${APP_VERSION} (빌드일: ${formattedDate})`;
    
    // 클릭 이벤트 리스너 추가
    versionElement.addEventListener('click', () => {
      console.log('버전 정보 클릭됨, 모달 표시 시도...');
      showVersionModal(APP_VERSION, BUILD_DATE);
    });
    
    // 테마 변경 감지를 위한 MutationObserver 설정
    setupThemeChangeObserver(versionElement);
    
    body.appendChild(versionElement);
  }
  
  // 설정 모달에도 버전 정보 표시
  const appVersionElement = document.getElementById('appVersion');
  const lastUpdateElement = document.getElementById('lastUpdate');
  
  if (appVersionElement) {
    appVersionElement.textContent = APP_VERSION;
  }
  
  if (lastUpdateElement) {
    lastUpdateElement.textContent = formattedDate;
  }
}

/**
 * 버전 요소의 테마를 업데이트
 * @param {HTMLElement} versionElement - 버전 정보를 표시하는 요소
 */
function updateVersionElementTheme(versionElement) {
  const isDarkMode = isDarkModeEnabled();
  
  // 클래스 초기화
  versionElement.classList.remove('text-muted', 'text-light', 'text-white-50');
  
  if (isDarkMode) {
    // 다크모드 텍스트 스타일
    versionElement.classList.add('text-white-50');
  } else {
    // 라이트모드 텍스트 스타일
    versionElement.classList.add('text-muted');
  }
}

/**
 * 테마 변경을 감지하여 버전 요소의 스타일을 업데이트
 * @param {HTMLElement} versionElement - 버전 정보를 표시하는 요소
 */
function setupThemeChangeObserver(versionElement) {
  // html 요소의 data-theme 속성 변화 감지
  const htmlObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === 'data-theme') {
        updateVersionElementTheme(versionElement);
      }
    });
  });
  
  htmlObserver.observe(document.documentElement, { attributes: true });
  
  // body 클래스 변화 감지
  const bodyObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === 'class') {
        updateVersionElementTheme(versionElement);
      }
    });
  });
  
  bodyObserver.observe(document.body, { attributes: true });
} 